
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaUserMd,
  FaUserNurse,
  FaNotesMedical,
  FaPlus,
  FaTimes,
  FaExclamationTriangle,
  FaPaperclip,
} from "react-icons/fa";
import IpdService from "../../../../Services/IpdService";

const PAGE_SIZE = 10;


const NOTE_TYPE_STYLES = {
  1: {
    label: "Doctor Notes",
    subLabel: "Consultant instructions & treatment plan",
    icon: <FaUserMd className="text-[#5b5bd6] text-[12px]" />,
    iconBg: "bg-[#eef2ff]",
  },
  2: {
    label: "Nurse Notes",
    subLabel: "Nursing observations & patient monitoring",
    icon: <FaUserNurse className="text-[#16a34a] text-[12px]" />,
    iconBg: "bg-[#dcfce7]",
  },
  DEFAULT: {
    label: "Other Notes",
    subLabel: "Clinical observations",
    icon: <FaNotesMedical className="text-[#6b7280] text-[12px]" />,
    iconBg: "bg-gray-100",
  },
};

const getNoteTypeStyle = (noteTypeId) =>
  NOTE_TYPE_STYLES[noteTypeId] || NOTE_TYPE_STYLES.DEFAULT;

const STATUS_STYLES = {
  COMPLETED: "bg-green-100 text-green-600",
  PENDING_DOCTOR_REVIEW: "bg-yellow-100 text-yellow-600",
  PENDING: "bg-yellow-100 text-yellow-600",
  IN_PROGRESS: "bg-blue-100 text-blue-600",
  NEEDS_ATTENTION: "bg-red-100 text-red-600",
};

const getStatusStyle = (status) =>
  STATUS_STYLES[status?.toUpperCase()] || "bg-gray-100 text-gray-600";

const PRIORITY_STYLES = {
  URGENT: "bg-orange-100 text-orange-600",
  CRITICAL: "bg-red-100 text-red-600",
  ROUTINE: "bg-blue-100 text-blue-600",
};

const getPriorityStyle = (priority) =>
  PRIORITY_STYLES[priority?.toUpperCase()] || "bg-blue-100 text-blue-600";

const formatDatetime = (value) => {
  if (!value) return "—";
  const d = new Date(value.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const displayStatusLabel = (status) => {
  if (!status) return "—";
  return status
    .toLowerCase()
    .split("_")
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
};

// ---------------------------------------------------------------------------
// Skeleton shown while a column is loading
// ---------------------------------------------------------------------------
function SkeletonNote() {
  return (
    <div className="bg-[#fafbff] border border-[#edf1f7] rounded-xl overflow-hidden animate-pulse">
      <div className="px-3 py-3 space-y-3">
        <div className="h-3 w-1/3 bg-gray-200 rounded" />
        <div className="h-2 w-1/4 bg-gray-200 rounded" />
        <div className="h-2 w-full bg-gray-200 rounded" />
        <div className="h-2 w-2/3 bg-gray-200 rounded" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail modal, populated via GET /clinicalNote/{noteId}
// ---------------------------------------------------------------------------
function NoteDetailModal({ noteId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchDetail() {
      setLoading(true);
      setError(null);
      try {
        const res = await IpdService.getClinicalNoteDetail(noteId);
        if (!cancelled) setDetail(res.data);
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load note");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [noteId]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-white rounded-[22px] overflow-hidden shadow-xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-[#edf1f7] flex items-center justify-between shrink-0">
          <h3 className="text-[15px] font-bold text-[#111827]">
            Clinical Note Details
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-[#6b7280]"
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          {loading && (
            <div className="space-y-3 animate-pulse">
              <div className="h-4 w-1/2 bg-gray-200 rounded" />
              <div className="h-3 w-full bg-gray-200 rounded" />
              <div className="h-3 w-full bg-gray-200 rounded" />
              <div className="h-3 w-2/3 bg-gray-200 rounded" />
            </div>
          )}

          {!loading && error && (
            <div className="flex items-start gap-3 text-[#b91c1c]">
              <FaExclamationTriangle className="mt-0.5" />
              <p className="text-[13px]">{error}</p>
            </div>
          )}

          {!loading && !error && detail && (
            <div className="space-y-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[13px] text-[#4b5563] leading-6">
                    {detail.noteText}
                  </p>
                </div>
                <span
                  className={`shrink-0 text-[10px] font-semibold px-2 py-1 rounded-md ${getStatusStyle(
                    detail.status
                  )}`}
                >
                  {displayStatusLabel(detail.status)}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Recorded By" value={detail.recordedByName} />
                <Field
                  label="Note Time"
                  value={formatDatetime(detail.noteDatetime)}
                />
                <Field label="Encounter ID" value={detail.encounterId} />
                <Field label="Patient ID" value={detail.patientId} />
                <Field
                  label="Nurse Action Required"
                  value={detail.nurseActionRequired === "Y" ? "Yes" : "No"}
                />
                <Field
                  label="Closed On"
                  value={formatDatetime(detail.closedDatetime)}
                />
              </div>

              {detail.nurseAction && (
                <div className="border border-[#edf1f7] rounded-xl p-4 bg-[#fafbff] space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-[12px] font-bold text-[#111827]">
                      Nurse Action
                    </h4>
                    <span
                      className={`text-[10px] font-semibold px-2 py-1 rounded-md ${getPriorityStyle(
                        detail.nurseAction.priority
                      )}`}
                    >
                      {detail.nurseAction.priority}
                    </span>
                  </div>

                  <p className="text-[12px] text-[#374151] leading-5">
                    {detail.nurseAction.actionInstructions}
                  </p>

                  <div className="grid grid-cols-2 gap-4">
                    <Field
                      label="Assigned Nurse"
                      value={detail.nurseAction.assignedNurseName}
                    />
                    <Field
                      label="Due"
                      value={formatDatetime(detail.nurseAction.dueDatetime)}
                    />
                    <Field
                      label="Overall Status"
                      value={displayStatusLabel(
                        detail.nurseAction.overallStatus
                      )}
                    />
                    <Field
                      label="Doctor Review"
                      value={displayStatusLabel(
                        detail.nurseAction.doctorReviewStatus
                      )}
                    />
                  </div>

                  {detail.nurseAction.doctorReviewComments && (
                    <div>
                      <p className="text-[11px] text-[#9ca3af]">
                        Doctor Review Comments
                      </p>
                      <p className="text-[12px] text-[#374151] mt-1 leading-5">
                        {detail.nurseAction.doctorReviewComments}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {detail.attachments && detail.attachments.length > 0 && (
                <div>
                  <p className="text-[11px] font-semibold text-[#374151] mb-2">
                    Attachments
                  </p>
                  <div className="space-y-2">
                    {detail.attachments.map((att) => (
                      <div
                        key={att.attachmentId}
                        className="flex items-center gap-2 text-[12px] text-[#374151] border border-[#edf1f7] rounded-lg px-3 py-2"
                      >
                        <FaPaperclip className="text-[#9ca3af] text-[11px]" />
                        <span className="truncate">{att.fileName}</span>
                        <span className="text-[10px] text-[#9ca3af] ml-auto shrink-0">
                          {att.fileSizeKb} KB
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[11px] text-[#9ca3af]">{label}</p>
      <p className="text-[13px] font-semibold text-[#111827] mt-1">
        {value ?? "—"}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Note card, shared by both columns
// ---------------------------------------------------------------------------
function NoteCard({ note, iconBg, icon, onView }) {
  const priority = note.nurseAction?.priority;

  return (
    <div className="bg-[#fafbff] border border-[#edf1f7] rounded-xl overflow-hidden">
      {/* TOP */}
      <div className="px-3 py-3 border-b border-[#edf1f7] flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-lg ${iconBg} flex items-center justify-center`}
            >
              {icon}
            </div>

            <div>
              <h3 className="text-[11px] font-bold text-[#111827]">
                {note.recordedByName}
              </h3>
              <p className="text-[10px] text-[#6b7280] mt-[2px]">
                {note.nurseActionRequired === "Y"
                  ? "Nurse action required"
                  : "No action required"}
              </p>
            </div>
          </div>

          <p className="text-[10px] text-[#9ca3af] mt-3">
            {formatDatetime(note.noteDatetime)}
          </p>
        </div>

        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <span
            className={`text-[9px] font-semibold px-2 py-1 rounded-md ${getStatusStyle(
              note.status
            )}`}
          >
            {displayStatusLabel(note.status)}
          </span>
          {priority && (
            <span
              className={`text-[9px] font-semibold px-2 py-1 rounded-md ${getPriorityStyle(
                priority
              )}`}
            >
              {priority}
            </span>
          )}
        </div>
      </div>

      {/* NOTE */}
      <div className="px-3 py-3">
        <p className="text-[11px] text-[#374151] leading-5 line-clamp-3">
          {note.noteText}
        </p>

        <div className="mt-3 flex items-center justify-between">
          {note.attachments && note.attachments.length > 0 ? (
            <span className="flex items-center gap-1 text-[10px] text-[#6b7280]">
              <FaPaperclip className="text-[9px]" />
              {note.attachments.length} attachment
              {note.attachments.length > 1 ? "s" : ""}
            </span>
          ) : (
            <span />
          )}

          <button
            onClick={() => onView(note.noteId)}
            className="text-[10px] font-semibold text-[#5b5bd6] hover:underline"
          >
            View
          </button>
        </div>
      </div>
    </div>
  );
}

function NoteColumn({ noteTypeId, notes, loading, onView }) {
  const style = getNoteTypeStyle(noteTypeId);

  return (
    <div className="bg-white border border-[#e9edf5] rounded-[18px] overflow-hidden">
      {/* HEADER */}
      <div className="px-4 py-3 border-b border-[#edf1f7] flex items-center gap-2">
        <div
          className={`w-8 h-8 rounded-lg ${style.iconBg} flex items-center justify-center`}
        >
          {style.icon}
        </div>

        <div>
          <h2 className="text-[14px] font-bold text-[#111827]">
            {style.label}
          </h2>
          <p className="text-[10px] text-[#6b7280] mt-[2px]">
            {style.subLabel}
          </p>
        </div>
      </div>

      {/* CONTENT */}
      <div className="p-4 space-y-3">
        {loading && (
          <>
            <SkeletonNote />
            <SkeletonNote />
          </>
        )}

        {!loading && notes.length === 0 && (
          <p className="text-[11px] text-[#9ca3af] text-center py-6">
            No notes found.
          </p>
        )}

        {!loading &&
          notes.map((note) => (
            <NoteCard
              key={note.noteId}
              note={note}
              icon={style.icon}
              iconBg={style.iconBg}
              onView={onView}
            />
          ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
function Notes({ patientId }) {
  const [openModal, setOpenModal] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Date filters sent to the API as "YYYY-MM-DD" (e.g. "2026-07-21").
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [activeNoteId, setActiveNoteId] = useState(null);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    const payload = {
      patientId,
      pageNo: 1,
      pageSize: PAGE_SIZE,
      ...(fromDate ? { fromDate } : {}),
      ...(toDate ? { toDate } : {}),
    };
    try {
      const res = await IpdService.getClinicalNotes(payload);
      setNotes(Array.isArray(res.data) ? res.data : res.data?.content || []);
    } catch (err) {
      setError(err.message || "Failed to load clinical notes");
    } finally {
      setLoading(false);
    }
  }, [patientId, fromDate, toDate]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const sortedNotes = useMemo(() => {
    return [...notes].sort(
      (a, b) => new Date(b.noteDatetime) - new Date(a.noteDatetime)
    );
  }, [notes]);

  const doctorNotes = useMemo(
    () => sortedNotes.filter((n) => n.noteTypeId === 1),
    [sortedNotes]
  );
  const nurseNotes = useMemo(
    () => sortedNotes.filter((n) => n.noteTypeId === 2),
    [sortedNotes]
  );

  const dateRangeInvalid =
    fromDate && toDate && new Date(fromDate) > new Date(toDate);

  const handleClearDates = () => {
    setFromDate("");
    setToDate("");
  };

  return (
    <div className="bg-white border border-[#e9edf5] rounded-[18px] overflow-hidden">
      {/* TOP HEADER */}
      <div className="px-4 py-4 border-b border-[#edf1f7] flex items-center justify-between flex-wrap gap-3">
        {/* LEFT */}
        <div>
          <h2 className="text-[16px] font-bold text-[#111827]">
            Doctor / Nurse Notes
          </h2>

          <p className="text-[11px] text-[#6b7280] mt-1">
            Doctor & nurse observations for inpatient treatment
          </p>
        </div>

        {/* DATE FILTERS */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <label className="text-[11px] font-semibold text-[#6b7280]">
              From
            </label>
            <input
              type="date"
              value={fromDate}
              max={toDate || undefined}
              onChange={(e) => setFromDate(e.target.value)}
              className="h-8 rounded-md border border-[#e5e7eb] bg-white px-3 text-[13px] font-semibold text-[#111827] outline-none focus:border-[#5b5bd6]"
            />
          </div>

          <div className="flex items-center gap-1.5">
            <label className="text-[11px] font-semibold text-[#6b7280]">
              To
            </label>
            <input
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(e) => setToDate(e.target.value)}
              className="h-8 rounded-md border border-[#e5e7eb] bg-white px-3 text-[13px] font-semibold text-[#111827] outline-none focus:border-[#5b5bd6]"
            />
          </div>

          {(fromDate || toDate) && (
            <button
              onClick={handleClearDates}
              className="h-8 px-3 rounded-md border border-[#e5e7eb] bg-white text-[12px] font-semibold text-[#6b7280] hover:bg-gray-50"
            >
              Clear
            </button>
          )}

          {/* Kept available for when the create-note endpoint is wired up */}
          {/* <button
            onClick={() => setOpenModal(true)}
            className="h-8 px-4 rounded-md bg-[#5b5bd6] text-white text-[11px] font-semibold flex items-center gap-2 hover:bg-[#4b4bc7]"
          >
            <FaPlus className="text-[10px]" />
            Add Notes
          </button> */}
        </div>
      </div>

      {dateRangeInvalid && (
        <div className="mx-4 mt-4 flex items-center gap-2 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-amber-700">
          <FaExclamationTriangle />
          <p className="text-[13px] font-medium">
            "From" date is after "To" date — adjust the range to see results.
          </p>
        </div>
      )}

      {error && (
        <div className="mx-4 mt-4 flex items-center justify-between gap-3 rounded-2xl border border-red-100 bg-red-50 px-4 py-3">
          <div className="flex items-center gap-2 text-[#b91c1c]">
            <FaExclamationTriangle />
            <p className="text-[13px] font-medium">{error}</p>
          </div>
          <button
            onClick={fetchNotes}
            className="h-8 px-3 rounded-lg bg-white border border-red-200 text-[12px] font-semibold text-[#b91c1c] hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      )}

      {/* CONTENT */}
      {!dateRangeInvalid && (
        <div className="grid grid-cols-2 gap-4 p-4">
          <NoteColumn
            noteTypeId={1}
            notes={doctorNotes}
            loading={loading}
            onView={setActiveNoteId}
          />
          <NoteColumn
            noteTypeId={2}
            notes={nurseNotes}
            loading={loading}
            onView={setActiveNoteId}
          />
        </div>
      )}

      {/* ADD-NOTE MODAL (static — wire up to a create endpoint when available) */}
      {openModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-5">
          <div className="bg-white rounded-[22px] w-full max-w-[700px] overflow-hidden">
            {/* HEADER */}
            <div className="px-5 py-4 border-b border-[#edf1f7] flex items-center justify-between">
              <div>
                <h2 className="text-[18px] font-bold text-[#111827]">
                  Add Clinical Notes
                </h2>

                <p className="text-[11px] text-[#6b7280] mt-1">
                  Add doctor or nurse observations
                </p>
              </div>

              <button
                onClick={() => setOpenModal(false)}
                className="w-9 h-9 rounded-lg bg-[#f4f6fb] flex items-center justify-center"
              >
                <FaTimes className="text-[12px]" />
              </button>
            </div>

            {/* BODY */}
            <div className="p-5 grid grid-cols-2 gap-4">
              {/* NOTE TYPE */}
              <div>
                <label className="text-[11px] font-semibold text-[#374151]">
                  Note Type
                </label>

                <select className="w-full h-[42px] rounded-lg border border-[#e5e7eb] mt-2 px-3 text-[11px] outline-none focus:border-[#5b5bd6]">
                  <option>Doctor Note</option>
                  <option>Nurse Note</option>
                </select>
              </div>

              {/* NAME */}
              <div>
                <label className="text-[11px] font-semibold text-[#374151]">
                  Name
                </label>

                <input
                  type="text"
                  placeholder="Enter name..."
                  className="w-full h-[42px] rounded-lg border border-[#e5e7eb] mt-2 px-3 text-[11px] outline-none focus:border-[#5b5bd6]"
                />
              </div>

              {/* PRIORITY */}
              <div>
                <label className="text-[11px] font-semibold text-[#374151]">
                  Priority
                </label>

                <select className="w-full h-[42px] rounded-lg border border-[#e5e7eb] mt-2 px-3 text-[11px] outline-none focus:border-[#5b5bd6]">
                  <option>Routine</option>
                  <option>Urgent</option>
                  <option>Critical</option>
                </select>
              </div>

              {/* TIME */}
              <div>
                <label className="text-[11px] font-semibold text-[#374151]">
                  Date & Time
                </label>

                <input
                  type="datetime-local"
                  className="w-full h-[42px] rounded-lg border border-[#e5e7eb] mt-2 px-3 text-[11px] outline-none focus:border-[#5b5bd6]"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-[#374151]">
                  Status
                </label>

                <select className="w-full h-[42px] rounded-lg border border-[#e5e7eb] mt-2 px-3 text-[11px] outline-none focus:border-[#5b5bd6]">
                  <option>Stable</option>
                  <option>Under Observation</option>
                  <option>Needs Attention</option>
                  <option>Critical</option>
                  <option>Medication Given</option>
                </select>
              </div>

              {/* NOTES */}
              <div className="col-span-2">
                <label className="text-[11px] font-semibold text-[#374151]">
                  Clinical Notes
                </label>

                <textarea
                  rows={2}
                  placeholder="Enter clinical observations..."
                  className="w-full rounded-xl border border-[#e5e7eb] mt-2 p-4 text-[11px] resize-none outline-none focus:border-[#5b5bd6]"
                />
              </div>
            </div>

            {/* FOOTER */}
            <div className="px-5 py-4 border-t border-[#edf1f7] flex justify-end gap-3">
              <button
                onClick={() => setOpenModal(false)}
                className="h-[38px] px-4 rounded-lg border border-[#e5e7eb] text-[11px] font-semibold text-[#374151]"
              >
                Cancel
              </button>

              <button className="h-[38px] px-5 rounded-lg bg-[#5b5bd6] text-white text-[11px] font-semibold">
                Save Notes
              </button>
            </div>
          </div>
        </div>
      )}

      {activeNoteId != null && (
        <NoteDetailModal
          noteId={activeNoteId}
          onClose={() => setActiveNoteId(null)}
        />
      )}
    </div>
  );
}

export default Notes;
